import { Account } from '../interfaces/accounting.interface';

/**
 * The chart uses 10-digit codes built in two-digit levels: level 1 has 2 significant digits
 * (e.g. 1000000000), level 2 has 4 (1001000000), level 3 has 6 ... level 5 has all 10.
 */
export const CODE_LENGTH = 10;
const SIG_LENGTH_BY_LEVEL = [2, 4, 6, 8, 10];

export function accountLevel(code: string): number {
  if (!code || code.length !== CODE_LENGTH) return 1;
  if (code.endsWith('00000000')) return 1;
  if (code.endsWith('000000')) return 2;
  if (code.endsWith('0000')) return 3;
  if (code.endsWith('00')) return 4;
  return 5;
}

/** Code with its trailing zeros removed - a child's code always starts with its ancestor's. */
export function sigPrefix(code: string): string {
  return code.replace(/0+$/, '') || code;
}

/**
 * The part of a code every descendant's code starts with. For 10-digit codes this is whole
 * two-digit levels (5003100000 -> "500310"), so a 10th group such as 5003100000 is not mistaken
 * for the parent of its 11th sibling 5003110000.
 */
function descendantPrefix(code: string): string {
  if (code.length !== CODE_LENGTH) return sigPrefix(code);
  return code.slice(0, SIG_LENGTH_BY_LEVEL[accountLevel(code) - 1]);
}

export function isDescendantAccount(child: { code: string }, parent: { code: string }): boolean {
  return child.code !== parent.code
    && accountLevel(child.code) > accountLevel(parent.code)
    && child.code.startsWith(descendantPrefix(parent.code));
}

/** A header (group) account has accounts under it and must not be posted to directly. */
export function isHeaderAccount(account: Account, all: Account[]): boolean {
  return all.some(o => o.id !== account.id
    && (o.parentAccountId === account.id || isDescendantAccount(o, account)));
}

/** Accounts that may receive postings: those with no sub-accounts. */
export function postingAccounts(all: Account[]): Account[] {
  return all.filter(a => !isHeaderAccount(a, all));
}

/** First 2*level digits of a parent's code - the part every child's code must start with. */
export function childPrefix(parent: Account): string {
  const level = accountLevel(parent.code);
  return parent.code.slice(0, SIG_LENGTH_BY_LEVEL[Math.min(level, 5) - 1]);
}

/** Suggests the next free code directly under `parent`, or '' when the level is full/unknown. */
export function suggestNextChildCode(parent: Account, all: Account[]): string {
  const level = accountLevel(parent.code);
  if (parent.code.length !== CODE_LENGTH || level >= 5) return '';
  const prefix = childPrefix(parent);
  const groupLen = SIG_LENGTH_BY_LEVEL[level] - prefix.length;
  let max = 0;
  all.forEach(a => {
    if (a.code.length === CODE_LENGTH && a.code.startsWith(prefix) && accountLevel(a.code) === level + 1) {
      const n = parseInt(a.code.slice(prefix.length, prefix.length + groupLen), 10);
      if (!Number.isNaN(n) && n > max) max = n;
    }
  });
  const next = max + 1;
  if (String(next).length > groupLen) return '';
  return (prefix + String(next).padStart(groupLen, '0')).padEnd(CODE_LENGTH, '0');
}

/** Inline validation message for a new/edited account code, or '' when it is acceptable. */
export function validateAccountCode(code: string, parent: Account | undefined): string {
  if (!/^\d+$/.test(code)) return 'Code must contain digits only.';
  if (code.length !== CODE_LENGTH) return `Code must be exactly ${CODE_LENGTH} digits, like the rest of the chart.`;
  if (parent) {
    if (parent.code.length === CODE_LENGTH) {
      const level = accountLevel(parent.code);
      if (level >= 5) return 'The selected parent is at the lowest level and cannot have sub-accounts.';
      if (!code.startsWith(childPrefix(parent))) {
        return `Code must start with ${childPrefix(parent)} to sit under ${parent.code}.`;
      }
      if (accountLevel(code) !== level + 1) {
        return `Code must be one level below the parent (level ${level + 1}).`;
      }
    }
  }
  return '';
}
